import React, { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { Search, Video as VideoIcon, ChevronLeft, ChevronRight } from "lucide-react";
import { api } from "@/lib/api";
import { Seo } from "@/components/site/Seo";
import { Reveal, Overline, Rule, Watermark } from "@/components/site/Primitives";
import { VideoCard } from "@/components/site/VideoCard";
import { VideoPlayer } from "@/components/site/VideoPlayer";
import { EmptyState } from "@/components/site/EmptyState";

export default function Videos() {
  const [params, setParams] = useSearchParams();
  const [topics, setTopics] = useState([]);
  const [featured, setFeatured] = useState(null);
  const [data, setData] = useState({ items: [], total: 0, pages: 1, page: 1 });
  const [loading, setLoading] = useState(true);
  const [searchInput, setSearchInput] = useState(params.get("search") || "");

  const topic = params.get("topic") || "";
  const search = params.get("search") || "";
  const page = parseInt(params.get("page") || "1", 10);

  useEffect(() => {
    api.get("/public/topics").then((r) => setTopics(r.data)).catch(() => {});
    api.get("/public/videos", { params: { featured: true, per_page: 1 } })
      .then((r) => setFeatured(r.data.items[0] || null)).catch(() => {});
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/public/videos", { params: { topic, search, page, per_page: 9 } });
      setData(data);
    } catch {
      setData({ items: [], total: 0, pages: 1, page: 1 });
    } finally {
      setLoading(false);
    }
  }, [topic, search, page]);

  useEffect(() => { load(); }, [load]);

  const update = (patch) => {
    const next = new URLSearchParams(params);
    Object.entries(patch).forEach(([k, v]) => { if (v) next.set(k, v); else next.delete(k); });
    if (!("page" in patch)) next.delete("page");
    setParams(next);
  };

  const showFeatured = featured && !topic && !search && page === 1;

  return (
    <>
      <Seo title="Videos" description="A library of short, practical videos on strategic planning, management and governance." />

      <section className="relative overflow-hidden border-b border-gold-500/20 bg-cream-200/40">
        <Watermark className="-right-6 top-2 text-[13rem]">Videos</Watermark>
        <div className="relative mx-auto max-w-7xl px-5 py-20 lg:px-10">
          <Overline>Watch &amp; Learn</Overline>
          <h1 className="mt-4 font-serif text-5xl font-light text-ink lg:text-6xl">Video Library</h1>
          <Rule className="mt-6" />
        </div>
      </section>

      {showFeatured && (
        <section className="mx-auto max-w-7xl px-5 py-16 lg:px-10">
          <div className="grid items-center gap-10 lg:grid-cols-2">
            <VideoPlayer video={featured} />
            <div>
              <Overline>Featured Video</Overline>
              <h2 className="mt-4 font-serif text-4xl font-light leading-tight text-ink">{featured.title}</h2>
              <p className="mt-4 font-sans leading-relaxed text-steel-600">{featured.description}</p>
            </div>
          </div>
        </section>
      )}

      <section className="mx-auto max-w-7xl px-5 py-14 lg:px-10">
        <div className="mb-12 flex flex-col gap-6 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex flex-wrap gap-2">
            <button onClick={() => update({ topic: "" })} className={`border px-4 py-2 font-sans text-sm ${!topic ? "border-gold-500 bg-gold-500 text-cream-100" : "border-ink/15 text-ink/70 hover:border-gold-500"}`} data-testid="filter-topic-all">All Topics</button>
            {topics.map((t) => (
              <button key={t} onClick={() => update({ topic: t })} className={`border px-4 py-2 font-sans text-sm ${topic === t ? "border-gold-500 bg-gold-500 text-cream-100" : "border-ink/15 text-ink/70 hover:border-gold-500"}`} data-testid={`filter-topic-${t}`}>{t}</button>
            ))}
          </div>
          <form onSubmit={(e) => { e.preventDefault(); update({ search: searchInput }); }} className="flex items-center gap-2 border-b border-ink/20 pb-2">
            <Search className="h-4 w-4 text-steel-500" />
            <input value={searchInput} onChange={(e) => setSearchInput(e.target.value)} placeholder="Search videos…" className="w-full bg-transparent font-sans text-sm text-ink placeholder:text-steel-400 focus:outline-none lg:w-56" data-testid="videos-search-input" />
          </form>
        </div>

        {loading ? (
          <p className="py-20 text-center font-sans text-steel-500">Loading…</p>
        ) : data.items.length === 0 ? (
          <div className="py-10">
            <EmptyState icon={VideoIcon} title="No videos yet" body="There are no published videos in this view. New videos are added regularly — please check back soon." />
          </div>
        ) : (
          <>
            <div className="grid gap-x-8 gap-y-14 sm:grid-cols-2 lg:grid-cols-3">
              {data.items.map((v, i) => (<Reveal key={v.id} delay={i * 0.04}><VideoCard video={v} /></Reveal>))}
            </div>
            {data.pages > 1 && (
              <div className="mt-16 flex items-center justify-center gap-2" data-testid="pagination">
                <button disabled={page <= 1} onClick={() => update({ page: String(page - 1), topic, search })} className="flex h-10 w-10 items-center justify-center border border-ink/15 disabled:opacity-30 hover:border-gold-500"><ChevronLeft className="h-4 w-4" /></button>
                {Array.from({ length: data.pages }, (_, i) => i + 1).map((p) => (
                  <button key={p} onClick={() => update({ page: String(p), topic, search })} className={`h-10 w-10 border font-sans text-sm ${p === page ? "border-gold-500 bg-gold-500 text-cream-100" : "border-ink/15 hover:border-gold-500"}`}>{p}</button>
                ))}
                <button disabled={page >= data.pages} onClick={() => update({ page: String(page + 1), topic, search })} className="flex h-10 w-10 items-center justify-center border border-ink/15 disabled:opacity-30 hover:border-gold-500"><ChevronRight className="h-4 w-4" /></button>
              </div>
            )}
          </>
        )}
      </section>
    </>
  );
}
